/**
 * Administration → Storage → Migrations → New migration (1.1.0): a wide dialog with the setup wizard's step rail.
 *   1. From and to: the source and destination locations, and a name.
 *   2. What: kinds, asset classes, ingest dates and last access, with live counts (POST /preview).
 *   3. How: move or copy, and when a move removes the sources.
 *   4. When and how fast: the window, transfers at once, bandwidth, daily caps, the fill stop, retries, the error pause.
 *   5. Review: plan (the dry run) with its progress, the report, then Start (a move needs the source's name typed) or
 *      Save as draft.
 * Opened from New migration, a location card's Migrate files… (?view=migrations&new=1&source=<id>), or a draft's Edit.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, Check, ClipboardList, Copy, Database, Gauge, Loader2, Play, Save, Search } from 'lucide-react';
import {
  DEFAULT_SCHEDULE_TIMEZONE, MIGRATION_FILE_KINDS, MigrationBody,
  type MigrationAction, type MigrationDTO, type MigrationFileKind, type MigrationPreviewBody, type MigrationSourceRemoval, type StorageLocationSummary,
} from '@prism/contracts';
import { api, errorDetail } from '../../../api/client';
import { isConfirmationRequired, useCategories, useMigration, useMigrationMutations, useMigrationPreview } from '../../../api/hooks';
import { Modal } from '../../../components/Modal';
import { Segmented } from '../../../components/Segmented';
import { toast } from '../../../components/Toasts';
import { bytes } from '../../../lib/format';
import { useDebounced } from '../../../lib/useDebounced';
import type { StatusDTO } from '../system/kit';
import { KindCounts, LimitsFields, countsLabel, PlanReportView, TypedName, limitsDraft, limitsFromDraft, limitsProblem, type LimitsDraft } from './MigrationParts';
import { useTransferWindows } from '../../../api/hooks/transferWindows';
import { FILE_KIND_LABEL, REMOVAL_LABEL, blockedKinds, previewState, startNeedsName, windowOfMigration, windowSummary } from './migrationText';

type StepId = 'locations' | 'what' | 'how' | 'when' | 'review';
const STEPS: Array<{ id: StepId; label: string; icon: ReactNode }> = [
  { id: 'locations', label: 'From and to', icon: <Database size={15} /> },
  { id: 'what', label: 'What moves', icon: <Search size={15} /> },
  { id: 'how', label: 'Move or copy', icon: <Copy size={15} /> },
  { id: 'when', label: 'When and how fast', icon: <Gauge size={15} /> },
  { id: 'review', label: 'Review', icon: <ClipboardList size={15} /> },
];

/** The form, as text where a field may be empty while typing. */
interface Draft {
  name: string;
  notes: string;
  sourceLocationId: string;
  destinationLocationId: string;
  action: MigrationAction;
  sourceRemoval: MigrationSourceRemoval;
  removalAfterDays: string;
  kinds: MigrationFileKind[];
  categoryIds: string[];
  ingestedFrom: string;
  ingestedTo: string;
  lastAccessDays: string;
  timezone: string;
  windowId: string | null;
  limits: LimitsDraft;
}

function fromDTO(m: MigrationDTO): Draft {
  return {
    name: m.name, notes: m.notes,
    sourceLocationId: m.source.id, destinationLocationId: m.destination.id,
    action: m.action, sourceRemoval: m.sourceRemoval, removalAfterDays: m.removalAfterDays == null ? '' : String(m.removalAfterDays),
    kinds: [...m.selection.kinds], categoryIds: [...m.selection.categoryIds],
    ingestedFrom: m.selection.ingestedFrom ?? '', ingestedTo: m.selection.ingestedTo ?? '',
    lastAccessDays: m.selection.lastAccessOlderThanDays == null ? '' : String(m.selection.lastAccessOlderThanDays),
    timezone: m.selection.timezone,
    windowId: m.window?.id ?? null,
    limits: limitsDraft(m.limits),
  };
}

function blank(sourceId: string): Draft {
  return {
    name: '', notes: '', sourceLocationId: sourceId, destinationLocationId: '',
    action: 'move', sourceRemoval: 'after_review', removalAfterDays: '',
    kinds: [...MIGRATION_FILE_KINDS], categoryIds: [], ingestedFrom: '', ingestedTo: '', lastAccessDays: '',
    timezone: DEFAULT_SCHEDULE_TIMEZONE, windowId: null, limits: limitsDraft(),
  };
}

function selectionOf(d: Draft): MigrationBody['selection'] {
  return {
    kinds: d.kinds, categoryIds: d.categoryIds,
    ingestedFrom: d.ingestedFrom || null, ingestedTo: d.ingestedTo || null,
    lastAccessOlderThanDays: d.lastAccessDays.trim() === '' ? null : Number(d.lastAccessDays),
    timezone: d.timezone,
  };
}

function bodyOf(d: Draft): MigrationBody {
  return {
    name: d.name.trim(), notes: d.notes,
    sourceLocationId: d.sourceLocationId, destinationLocationId: d.destinationLocationId,
    action: d.action, sourceRemoval: d.sourceRemoval,
    removalAfterDays: d.action === 'move' && d.sourceRemoval === 'after_days' && d.removalAfterDays.trim() !== '' ? Number(d.removalAfterDays) : null,
    selection: selectionOf(d), limits: limitsFromDraft(d.limits), windowId: d.windowId,
  };
}

const STEP_OF_FIELD: Record<string, StepId> = {
  name: 'locations', sourceLocationId: 'locations', destinationLocationId: 'locations', notes: 'locations',
  selection: 'what', action: 'how', sourceRemoval: 'how', removalAfterDays: 'how', limits: 'when', windowId: 'when',
};

/** The problems of each step (the contracts' own validation, attributed to the step that holds the field). */
function problemsOf(d: Draft): Partial<Record<StepId, string>> {
  const out: Partial<Record<StepId, string>> = {};
  if (!d.sourceLocationId || !d.destinationLocationId) out.locations = 'Choose the source and the destination';
  const limits = limitsProblem(d.limits);
  if (limits) out.when = limits;
  const r = MigrationBody.safeParse(bodyOf(d));
  if (!r.success) {
    for (const i of r.error.issues) {
      const step = STEP_OF_FIELD[String(i.path[0])] ?? 'locations';
      if (!out[step]) out[step] = step === 'locations' && i.path[0] === 'name' ? 'Give the migration a name' : i.message;
    }
  }
  return out;
}

/** Where a migration may write: enabled storage, PRISM-managed when on an agent. */
const canReceive = (l: StorageLocationSummary) => l.purpose === 'storage' && l.enabled && (l.kind !== 'agent' || l.agentFolder?.mode === 'managed' || l.agentRoot?.mode === 'managed');
/** An agent folder catalogued in place: its files are never removed by PRISM, so only a copy. */
const inPlace = (l: StorageLocationSummary | undefined) => l?.agentFolder?.mode === 'in_place' || l?.agentRoot?.mode === 'in_place';

export function MigrationWizard({ locations, initial, sourceId, onClose, onStarted }: {
  locations: StorageLocationSummary[];
  /** A draft or planned migration to continue. */
  initial?: MigrationDTO;
  /** The source from Migrate files…. */
  sourceId?: string | null;
  onClose: () => void;
  onStarted: (id: string) => void;
}) {
  const storage = locations.filter((l) => l.purpose === 'storage');
  const [d, setD] = useState<Draft>(() => (initial ? fromDTO(initial) : blank(sourceId && storage.some((l) => l.id === sourceId) ? sourceId : '')));
  const [step, setStep] = useState<StepId>(initial?.status === 'planned' ? 'review' : 'locations');
  const [savedId, setSavedId] = useState<string | null>(initial?.id ?? null);
  // The body the current plan was made for: a planned migration whose form is unchanged can start.
  const [plannedFor, setPlannedFor] = useState<string | null>(initial?.status === 'planned' ? JSON.stringify(bodyOf(fromDTO(initial))) : null);
  const [nameTouched, setNameTouched] = useState(Boolean(initial));
  const [typed, setTyped] = useState('');
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'save' | 'plan' | 'start' | null>(null);
  const muts = useMigrationMutations();
  const saved = useMigration(savedId);
  const categories = useCategories().data?.items ?? [];
  const status = useQuery({ queryKey: ['system-status'], queryFn: () => api<StatusDTO>('/api/v1/system/status'), staleTime: 60_000 });
  const slots = status.data?.workflowService?.admission?.transfer.max ?? null;
  const windows = useTransferWindows().data?.items ?? [];

  const source = storage.find((l) => l.id === d.sourceLocationId);
  const destination = storage.find((l) => l.id === d.destinationLocationId);
  const destinations = storage.filter((l) => l.id !== d.sourceLocationId && canReceive(l));
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((x) => ({ ...x, [k]: v }));

  // A suggested name until the admin types one.
  useEffect(() => {
    if (nameTouched || !source || !destination) return;
    setD((x) => ({ ...x, name: `${x.action === 'copy' ? 'Copy' : 'Move'} ${source.name} to ${destination.name}` }));
  }, [nameTouched, source, destination, d.action]);
  // A source catalogued in place can only be copied from.
  useEffect(() => { if (inPlace(source) && d.action === 'move') set('action', 'copy'); }, [source, d.action]);
  // Kinds the API would refuse (a copy, an agent or an archive at either end) are unticked and disabled, with why.
  const blocked = useMemo(() => blockedKinds(d.action, source, destination), [d.action, source, destination]);
  useEffect(() => {
    if (d.kinds.some((k) => blocked[k])) setD((x) => ({ ...x, kinds: x.kinds.filter((k) => !blocked[k]) }));
  }, [blocked, d.kinds]);

  const problems = useMemo(() => problemsOf(d), [d]);
  const body = useMemo(() => bodyOf(d), [d]);
  const bodyKey = JSON.stringify(body);
  const index = STEPS.findIndex((x) => x.id === step);
  const firstProblem = STEPS.find((x) => problems[x.id]);

  // Live counts for the "what" step, debounced while typing.
  // Memoized on its JSON, so the debounce (by identity) settles and "Counting…" ends once it has.
  const previewKey = !problems.locations && !problems.what && d.destinationLocationId
    ? JSON.stringify({ sourceLocationId: d.sourceLocationId, destinationLocationId: d.destinationLocationId, action: d.action, selection: selectionOf(d) } satisfies MigrationPreviewBody) : '';
  const previewBody = useMemo(() => (previewKey ? JSON.parse(previewKey) as MigrationPreviewBody : null), [previewKey]);
  const debounced = useDebounced(previewBody, 500);
  const debouncedKey = debounced ? JSON.stringify(debounced) : '';
  const preview = useMigrationPreview(step === 'what' || step === 'how' ? debounced : null);
  const previewView = previewState({ isFetching: preview.isFetching, isError: preview.isError, hasData: Boolean(preview.data) }, previewKey, debouncedKey);

  const m = saved.data;
  const planning = m?.status === 'planning' || busy === 'plan';
  const planFresh = Boolean(m && m.status === 'planned' && plannedFor === bodyKey);
  // The chosen window for the summary: from the list, else the saved migration's own (the list may still be loading).
  const chosenWindow = d.windowId ? windows.find((w) => w.id === d.windowId) ?? (m?.window?.id === d.windowId ? m.window : undefined) : null;

  /** Creates the draft, or saves the form into it (everything but the locations). */
  async function save(): Promise<string> {
    if (!savedId) {
      const created = await muts.create.mutateAsync(body);
      setSavedId(created.id);
      return created.id;
    }
    const { sourceLocationId: _s, destinationLocationId: _d, ...patch } = body;
    void _s; void _d;
    await muts.update.mutateAsync({ id: savedId, body: patch });
    return savedId;
  }

  async function saveDraft() {
    setBusy('save');
    try {
      await save();
      toast(`Saved ${body.name} as a draft`);
      onClose();
    } catch (err) {
      toast(errorDetail(err), 'error');
    } finally {
      setBusy(null);
    }
  }

  async function plan() {
    setBusy('plan');
    try {
      const id = await save();
      await muts.plan.mutateAsync({ id });
      setPlannedFor(bodyKey);
      await saved.refetch();
    } catch (err) {
      toast(errorDetail(err), 'error');
    } finally {
      setBusy(null);
    }
  }

  async function start() {
    if (!savedId || !m) return;
    setBusy('start');
    setConfirmError(null);
    try {
      await muts.start.mutateAsync({ id: savedId, body: startNeedsName(m) ? { confirm: typed } : {} });
      toast(`Started ${m.name}`);
      onStarted(savedId);
    } catch (err) {
      if (isConfirmationRequired(err)) setConfirmError(`Type the source location’s name exactly: ${m.source.name}`);
      else toast(errorDetail(err), 'error');
    } finally {
      setBusy(null);
    }
  }

  const footer = (
    <>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <span style={{ flex: 1 }} />
      <button className="btn" disabled={busy !== null || Boolean(firstProblem)} onClick={() => void saveDraft()} data-testid="mig-save-draft" title={firstProblem ? problems[firstProblem.id] : undefined}><Save size={14} /> Save as draft</button>
      {index > 0 && <button className="btn" onClick={() => setStep(STEPS[index - 1]!.id)} data-testid="mig-back"><ArrowLeft size={14} /> Back</button>}
      {step !== 'review'
        ? <button className="btn primary" disabled={Boolean(problems[step])} onClick={() => setStep(STEPS[index + 1]!.id)} data-testid="mig-next">Next <ArrowRight size={14} /></button>
        : planFresh
          ? <button className="btn primary" disabled={busy !== null || (startNeedsName(m!) && typed !== m!.source.name)} onClick={() => void start()} data-testid="mig-start">
            {busy === 'start' ? <Loader2 size={14} className="spin" /> : <Play size={14} />} Start {m!.action === 'move' ? 'moving' : 'copying'}</button>
          : <button className="btn primary" disabled={busy !== null || planning || Boolean(firstProblem)} onClick={() => void plan()} data-testid="mig-plan">
            {planning ? <Loader2 size={14} className="spin" /> : <ClipboardList size={14} />} {m?.status === 'planned' ? 'Plan again' : 'Plan (dry run)'}</button>}
    </>
  );

  return (
    <Modal wide title={initial ? `Migration: ${initial.name}` : 'New migration'} onClose={onClose} actions={footer}>
      <div className="mig-wizard" data-testid="mig-wizard">
        <ol className="setup-steps mig-rail">
          {STEPS.map((x, i) => (
            <li key={x.id} className={x.id === step ? 'current' : i < index && !problems[x.id] ? 'done' : ''}>
              <button type="button" onClick={() => setStep(x.id)} data-testid={`mig-step-${x.id}`}>{i < index && !problems[x.id] ? <Check size={15} /> : x.icon}{x.label}</button>
            </li>
          ))}
        </ol>
        <div className="mig-step">
          {step === 'locations' && (
            <section className="mig-section">
              <p className="muted">Files move from one storage location to another, each copied and verified by checksum. Nothing moves until you review the plan and start it.</p>
              <div className="field-row">
                <div className="field"><label htmlFor="mig-source">From</label>
                  <select id="mig-source" className="select" value={d.sourceLocationId} disabled={Boolean(savedId)} onChange={(e) => set('sourceLocationId', e.target.value)} data-testid="mig-source">
                    <option value="">Choose the source…</option>
                    {storage.map((l) => <option key={l.id} value={l.id}>{l.name} · {l.usage.files.toLocaleString('en-US')} files, {bytes(l.usage.bytes)}</option>)}
                  </select></div>
                <div className="field"><label htmlFor="mig-dest">To</label>
                  <select id="mig-dest" className="select" value={d.destinationLocationId} disabled={Boolean(savedId)} onChange={(e) => set('destinationLocationId', e.target.value)} data-testid="mig-dest">
                    <option value="">Choose the destination…</option>
                    {destinations.map((l) => <option key={l.id} value={l.id}>{l.name} · {l.site}{l.archive?.cold ? ' (archive: originals only)' : ''}</option>)}
                  </select>
                  {savedId && <span className="help">The locations of a saved migration stay as they are; make a new migration for others.</span>}</div>
              </div>
              <div className="field"><label htmlFor="mig-name">Name</label>
                <input id="mig-name" className="input" value={d.name} maxLength={120} onChange={(e) => { setNameTouched(true); set('name', e.target.value); }} data-testid="mig-name" /></div>
              <div className="field"><label htmlFor="mig-notes">Notes <span className="faint">(optional)</span></label>
                <textarea id="mig-notes" className="input" rows={2} maxLength={4000} value={d.notes} onChange={(e) => set('notes', e.target.value)} /></div>
              {destination?.archive?.cold && <span className="help">An archive destination takes originals only; each is verified by the transfer’s checksum.</span>}
            </section>
          )}

          {step === 'what' && (
            <section className="mig-section">
              <div className="field"><label>Kinds of files</label>
                <div className="mig-checks">
                  {MIGRATION_FILE_KINDS.map((k) => (
                    <label key={k} className="toggle-row" title={blocked[k]}><input type="checkbox" checked={d.kinds.includes(k) && !blocked[k]} disabled={Boolean(blocked[k])} data-testid={`mig-kind-${k}`}
                      onChange={(e) => set('kinds', e.target.checked ? MIGRATION_FILE_KINDS.filter((x) => x === k || d.kinds.includes(x)) : d.kinds.filter((x) => x !== k))} /><span>{FILE_KIND_LABEL[k]}</span></label>
                  ))}
                </div>
                {[...new Set(Object.values(blocked))].map((why) => <span key={why} className="help" data-testid="mig-kind-blocked">{why}</span>)}
                <span className="help">Collateral (attachments) is moved file by file, never copied, and never to or from a storage agent or an archive.</span></div>
              <div className="field"><label>Asset classes</label>
                {categories.length === 0 ? <span className="faint">Every asset (there are no asset classes).</span> : (
                  <div className="mig-classes" data-testid="mig-classes">
                    {categories.map((c) => (
                      <label key={c.id} className="toggle-row"><input type="checkbox" checked={d.categoryIds.includes(c.id)}
                        onChange={(e) => set('categoryIds', e.target.checked ? [...d.categoryIds, c.id] : d.categoryIds.filter((x) => x !== c.id))} />
                        <span><span className="mig-swatch" style={{ background: c.color }} />{c.name}</span></label>
                    ))}
                  </div>
                )}
                <span className="help">{d.categoryIds.length ? `${d.categoryIds.length} chosen.` : 'None chosen: every class.'}</span></div>
              <div className="field-row three">
                <div className="field"><label htmlFor="mig-from">Ingested from</label><input id="mig-from" className="input" type="date" value={d.ingestedFrom} onChange={(e) => set('ingestedFrom', e.target.value)} data-testid="mig-ingested-from" /></div>
                <div className="field"><label htmlFor="mig-to">Ingested until</label><input id="mig-to" className="input" type="date" value={d.ingestedTo} onChange={(e) => set('ingestedTo', e.target.value)} data-testid="mig-ingested-to" /></div>
                <div className="field"><label htmlFor="mig-access">Not used for</label>
                  <div className="mig-unit"><input id="mig-access" className="input" type="number" min={0} placeholder="Any" value={d.lastAccessDays} onChange={(e) => set('lastAccessDays', e.target.value)} data-testid="mig-last-access" /><span className="faint">days</span></div></div>
              </div>
              <span className="help">Dates are local dates in {d.timezone}, both included. “Not used” counts playback, downloads, exports and processing since the file arrived. Every version on the source is included, oldest ingest first.</span>
              {problems.what && <span className="error-text">{problems.what}</span>}
              <PreviewCounts q={preview} view={previewView} />
            </section>
          )}

          {step === 'how' && (
            <section className="mig-section">
              <div className="field"><label>Action</label>
                <Segmented role="radiogroup" label="Action" buttonType="button" value={d.action} onChange={(v) => set('action', v)} options={[
                  { value: 'move', label: 'Move', testId: 'mig-action-move' }, { value: 'copy', label: 'Copy', testId: 'mig-action-copy' },
                ]} />
                <span className="help">{inPlace(source) ? 'The source is catalogued in place on a storage agent: PRISM never removes files there, so it can only copy.' : d.action === 'move' ? 'Each file is copied and verified, then removed from the source.' : 'Each file is copied and verified; the source keeps its copy. Files already at the destination are skipped.'}</span></div>
              {d.action === 'move' && (
                <div className="field"><label>Remove the source files</label>
                  <div className="mig-radios">
                    {(['after_review', 'per_file', 'after_days'] as const).map((r) => (
                      <label key={r} className="toggle-row"><input type="radio" name="mig-removal" checked={d.sourceRemoval === r} onChange={() => set('sourceRemoval', r)} data-testid={`mig-removal-${r}`} />
                        <span>{REMOVAL_LABEL[r]}{r === 'after_review' && <span className="faint"> (recommended)</span>}</span></label>
                    ))}
                  </div>
                  {d.sourceRemoval === 'after_days' && (
                    <div className="field" style={{ maxWidth: 220 }}><label htmlFor="mig-days">Days after verification</label>
                      <input id="mig-days" className="input" type="number" min={1} value={d.removalAfterDays} onChange={(e) => set('removalAfterDays', e.target.value)} data-testid="mig-removal-days" /></div>
                  )}
                  <span className="help">PRISM never removes a file’s last copy, and checks the destination’s checksum again just before removing.</span></div>
              )}
              {problems.how && <span className="error-text">{problems.how}</span>}
              <PreviewCounts q={preview} view={previewView} />
            </section>
          )}

          {step === 'when' && (
            <section className="mig-section">
              <LimitsFields value={d.limits} onChange={(v) => set('limits', v)} windowId={d.windowId} onWindow={(v) => set('windowId', v)} transferSlots={slots} />
              {problems.when && <span className="error-text" data-testid="mig-limits-error">{problems.when}</span>}
            </section>
          )}

          {step === 'review' && (
            <section className="mig-section" data-testid="mig-review">
              <dl className="kv mig-summary">
                <dt>From</dt><dd>{source?.name ?? '—'}</dd>
                <dt>To</dt><dd>{destination?.name ?? '—'}</dd>
                <dt>Action</dt><dd>{d.action === 'move' ? `Move · sources removed ${({ after_review: 'after review', per_file: 'per file', after_days: `${d.removalAfterDays || 'N'} days after verification` })[d.sourceRemoval]}` : 'Copy'}</dd>
                <dt>Files</dt><dd>{d.kinds.map((k) => FILE_KIND_LABEL[k]).join(', ')}{d.categoryIds.length ? ` · ${d.categoryIds.length} asset class${d.categoryIds.length === 1 ? '' : 'es'}` : ''}{d.ingestedFrom || d.ingestedTo ? ` · ingested ${d.ingestedFrom || '…'} to ${d.ingestedTo || '…'}` : ''}{d.lastAccessDays ? ` · not used for ${d.lastAccessDays} days` : ''}</dd>
                <dt>Window</dt><dd data-testid="mig-review-window">{chosenWindow === undefined ? '…' : windowSummary(chosenWindow)}</dd>
                <dt>Limits</dt><dd>{d.limits.maxConcurrent} at once{d.limits.bandwidth ? ` · ${d.limits.bandwidth} MB/s` : ''}{d.limits.filesPerDay ? ` · ${d.limits.filesPerDay} files a day` : ''}{d.limits.gbPerDay ? ` · ${d.limits.gbPerDay} GB a day` : ''}{d.limits.stopAtPct ? ` · stop at ${d.limits.stopAtPct} %` : ''}</dd>
              </dl>
              {firstProblem && <span className="error-text">{firstProblem.label}: {problems[firstProblem.id]}</span>}
              {!m || (m.status === 'draft' && !planning) ? (
                <p className="muted" data-testid="mig-plan-hint">Plan it first: a dry run lists every file that would move, what is skipped and why, whether the destination has room, and how long it should take. Nothing moves yet.</p>
              ) : planning ? (
                <div className="mig-planning" data-testid="mig-planning"><Loader2 size={15} className="spin" /> Planning{m?.counters.total.files ? `: ${m.counters.total.files.toLocaleString('en-US')} files so far` : '…'}<div className="progress indeterminate"><span /></div></div>
              ) : m.status === 'failed' ? (
                <div className="banner error"><div className="banner-body"><strong>Planning failed</strong><span>{m.planError ?? 'Unknown error'}</span></div></div>
              ) : m.planReport ? (
                <>
                  {!planFresh && <div className="banner warning"><div className="banner-body"><span>The form changed since this plan: plan again to start.</span></div></div>}
                  <PlanReportView report={m.planReport} window={windowOfMigration(m)} />
                  {planFresh && startNeedsName(m) && <TypedName name={m.source.name} value={typed} onChange={(v) => { setTyped(v); setConfirmError(null); }} error={confirmError} />}
                  {planFresh && !startNeedsName(m) && <span className="help">A copy starts without confirmation: nothing is removed.</span>}
                </>
              ) : null}
            </section>
          )}
        </div>
      </div>
    </Modal>
  );
}

function PreviewCounts({ q, view }: { q: ReturnType<typeof useMigrationPreview>; view: ReturnType<typeof previewState> }) {
  if (view === 'hidden') return null;
  return (
    <div className="mig-preview" data-testid="mig-preview" data-state={view}>
      <p className="section-label">{view === 'counting' ? <><Loader2 size={11} className="spin" /> Counting…</> : view === 'error' ? 'Live counts' : 'On the source now'}</p>
      {view === 'error' ? <span className="error-text" data-testid="mig-preview-error">Couldn’t count: {errorDetail(q.error)}</span> : q.data && (
        <>
          <div className="mig-preview-total" data-testid="mig-preview-total">{q.data.partial ? 'At least ' : ''}{countsLabel(q.data)}</div>
          <KindCounts byKind={q.data.byKind} skipped={q.data.skipped} />
          {q.data.partial && <span className="help">Counting stopped after 5 seconds; planning counts everything.</span>}
        </>
      )}
    </div>
  );
}
